import React, { useState, useEffect, useRef } from 'react';
import {
  Row, Col, Card, Button, Form, Table, Badge, Modal,
  Alert, Spinner, ButtonGroup, Dropdown, OverlayTrigger, Tooltip,
} from 'react-bootstrap';
import {
  Plus, PencilSquare, Trash, Eye, EyeSlash,
  ArrowUp, ArrowDown, GripVertical, Image, TypeH2,
  ExclamationDiamond, Dash, Megaphone, PeopleFill,
} from 'react-bootstrap-icons';
import { AlignLeft } from 'lucide-react';
import PageLayout from '../../layouts/PageLayout';
import { pagesAPI, cloudinaryService } from '../../services';
import { toast } from 'react-toastify';

const SECTION_TYPES = [
  { value: 'HEADING', label: 'Tiêu đề', icon: <TypeH2 />, color: 'primary' },
  { value: 'TEXT', label: 'Văn bản (HTML)', icon: <AlignLeft />, color: 'secondary' },
  { value: 'IMAGE', label: 'Hình ảnh', icon: <Image />, color: 'success' },
  { value: 'NOTICE', label: 'Thông báo nổi bật', icon: <ExclamationDiamond />, color: 'warning' },
  { value: 'DIVIDER', label: 'Đường kẻ phân cách', icon: <Dash />, color: 'light' },
];

const CATEGORY_OPTIONS = ['GENERAL', 'POLICY', 'INFO', 'ANNOUNCEMENT', 'TRAINING'];

// ─── Section Preview ──────────────────────────────────────────────────────────
function SectionPreview({ section }) {
  switch (section.type) {
    case 'HEADING':
      return <h5 className="mb-0 text-primary">{section.content || '(Chưa có tiêu đề)'}</h5>;
    case 'TEXT':
      return (
        <div
          className="small"
          style={{ maxHeight: 60, overflow: 'hidden', opacity: 0.8 }}
          dangerouslySetInnerHTML={{ __html: section.content || '<em>(Chưa có nội dung)</em>' }}
        />
      );
    case 'IMAGE':
      return section.content
        ? <img src={section.content} alt="" style={{ maxHeight: 60, maxWidth: 120, objectFit: 'cover', borderRadius: 4 }} />
        : <span className="text-muted small">(Chưa có hình ảnh)</span>;
    case 'NOTICE':
      return (
        <div className="bg-warning bg-opacity-10 border-start border-warning border-3 px-2 py-1 rounded small" style={{ maxHeight: 60, overflow: 'hidden' }}>
          {section.content || '(Chưa có nội dung)'}
        </div>
      );
    case 'DIVIDER':
      return <hr className="my-1" />;
    default:
      return null;
  }
}

// ─── Section Editor Modal ─────────────────────────────────────────────────────
function SectionEditorModal({ show, onHide, section, pageId, onSaved }) {
  const [type, setType] = useState(section?.type || 'TEXT');
  const [content, setContent] = useState(section?.content || '');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  useEffect(() => {
    if (show) {
      setType(section?.type || 'TEXT');
      setContent(section?.content || '');
    }
  }, [show, section]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const url = await cloudinaryService.uploadImage(file);
      setContent(url);
      toast.success('Upload hình ảnh thành công');
    } catch (err) {
      toast.error('Upload thất bại: ' + (err?.message || 'Lỗi không xác định'));
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = { type, content };
      if (section?.id) {
        await pagesAPI.updateSection(pageId, section.id, data);
        toast.success('Đã cập nhật section');
      } else {
        await pagesAPI.addSection(pageId, data);
        toast.success('Đã thêm section');
      }
      onSaved();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu section');
    } finally {
      setSaving(false);
    }
  };

  const typeInfo = SECTION_TYPES.find((t) => t.value === type);

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>{section?.id ? 'Chỉnh sửa section' : 'Thêm section mới'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Loại section</Form.Label>
          <div className="d-flex flex-wrap gap-2">
            {SECTION_TYPES.map((t) => (
              <Button
                key={t.value}
                variant={type === t.value ? t.color : `outline-${t.color}`}
                size="sm"
                onClick={() => { setType(t.value); if (t.value === 'DIVIDER') setContent(''); }}
                className="d-flex align-items-center gap-1"
              >
                {t.icon} {t.label}
              </Button>
            ))}
          </div>
        </Form.Group>

        {type !== 'DIVIDER' && (
          <Form.Group className="mb-3">
            <Form.Label>
              {type === 'HEADING' && 'Nội dung tiêu đề'}
              {type === 'TEXT' && 'Nội dung HTML (rich text)'}
              {type === 'IMAGE' && 'URL hình ảnh'}
              {type === 'NOTICE' && 'Nội dung thông báo'}
            </Form.Label>

            {type === 'IMAGE' ? (
              <div className="d-flex flex-column gap-2">
                <div className="d-flex gap-2">
                  <Form.Control
                    type="text"
                    placeholder="Nhập URL hình ảnh hoặc upload..."
                    value={content}
                    onChange={(e) => setContent(e.target.value)}
                  />
                  <Button
                    variant="outline-success"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading}
                  >
                    {uploading ? <Spinner size="sm" /> : <Image />}
                  </Button>
                  <input ref={fileRef} type="file" accept="image/*" className="d-none" onChange={handleUpload} />
                </div>
                {content && (
                  <img src={content} alt="preview" style={{ maxHeight: 200, maxWidth: '100%', objectFit: 'contain', borderRadius: 8, border: '1px solid #dee2e6' }} />
                )}
              </div>
            ) : type === 'TEXT' ? (
              <>
                <Form.Control
                  as="textarea"
                  rows={8}
                  placeholder="Nhập HTML. Ví dụ: <p>Nội dung...</p> <ul><li>Mục 1</li></ul>"
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  style={{ fontFamily: 'monospace', fontSize: 13 }}
                />
                {content && (
                  <div className="mt-2 p-3 border rounded bg-light small">
                    <strong className="text-muted d-block mb-1">Preview:</strong>
                    <div dangerouslySetInnerHTML={{ __html: content }} />
                  </div>
                )}
              </>
            ) : (
              <Form.Control
                type="text"
                placeholder={type === 'HEADING' ? 'Ví dụ: Nội Quy Chung' : 'Ví dụ: Lưu ý quan trọng...'}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            )}
          </Form.Group>
        )}

        {type === 'DIVIDER' && (
          <Alert variant="secondary" className="py-2">
            Đường kẻ phân cách không có nội dung, dùng để ngăn cách các phần.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Huỷ</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}
          {section?.id ? 'Cập nhật' : 'Thêm'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Page Editor Modal ────────────────────────────────────────────────────────
function PageEditorModal({ show, onHide, page, onSaved }) {
  const [formData, setFormData] = useState({ slug: '', title: '', description: '', category: 'GENERAL', order: 0, is_published: false, requires_acknowledgement: false });
  const [saving, setSaving] = useState(false);
  const isEdit = !!page?.id;

  useEffect(() => {
    if (show) {
      setFormData(page
        ? { slug: page.slug, title: page.title, description: page.description || '', category: page.category || 'GENERAL', order: page.order || 0, is_published: page.is_published || false, requires_acknowledgement: page.requires_acknowledgement || false }
        : { slug: '', title: '', description: '', category: 'GENERAL', order: 0, is_published: false, requires_acknowledgement: false }
      );
    }
  }, [show, page]);

  const handleChange = (field, value) => setFormData((prev) => ({ ...prev, [field]: value }));

  const autoSlug = (title) => title.toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd').replace(/[^a-z0-9\s-]/g, '')
    .trim().replace(/\s+/g, '-');

  const handleSave = async () => {
    if (!formData.slug || !formData.title) {
      toast.error('Vui lòng điền slug và tiêu đề');
      return;
    }
    try {
      setSaving(true);
      if (isEdit) {
        await pagesAPI.updatePage(page.id, formData);
        toast.success('Đã cập nhật trang');
      } else {
        await pagesAPI.createPage(formData);
        toast.success('Đã tạo trang mới');
      }
      onSaved();
      onHide();
    } catch (err) {
      toast.error(err?.message || 'Không thể lưu trang');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton>
        <Modal.Title>{isEdit ? 'Chỉnh sửa thông tin trang' : 'Tạo trang mới'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Tiêu đề <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="Ví dụ: Nội Quy Nhân Viên"
            value={formData.title}
            onChange={(e) => {
              handleChange('title', e.target.value);
              if (!isEdit) handleChange('slug', autoSlug(e.target.value));
            }}
          />
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Slug (URL) <span className="text-danger">*</span></Form.Label>
          <Form.Control
            type="text"
            placeholder="noi-quy-nhan-vien"
            value={formData.slug}
            onChange={(e) => handleChange('slug', e.target.value)}
          />
          <Form.Text className="text-muted">Chỉ chữ thường, số và dấu gạch ngang. Dùng trong URL: /info/{formData.slug || '...'}</Form.Text>
        </Form.Group>
        <Form.Group className="mb-3">
          <Form.Label>Mô tả ngắn</Form.Label>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Mô tả hiển thị ở trang danh sách..."
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
          />
        </Form.Group>
        <Row>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Danh mục</Form.Label>
              <Form.Select value={formData.category} onChange={(e) => handleChange('category', e.target.value)}>
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </Form.Select>
            </Form.Group>
          </Col>
          <Col>
            <Form.Group className="mb-3">
              <Form.Label>Thứ tự</Form.Label>
              <Form.Control
                type="number"
                value={formData.order}
                onChange={(e) => handleChange('order', parseInt(e.target.value) || 0)}
              />
            </Form.Group>
          </Col>
        </Row>
        <Form.Check
          type="switch"
          label="Xuất bản ngay"
          checked={formData.is_published}
          onChange={(e) => handleChange('is_published', e.target.checked)}
        />
        <Form.Check
          type="switch"
          className="mt-2"
          label={<>Yêu cầu nhân viên xác nhận đã đọc <Badge bg="warning" text="dark" className="ms-1 small">Bắt buộc đọc</Badge></>}
          checked={formData.requires_acknowledgement}
          onChange={(e) => handleChange('requires_acknowledgement', e.target.checked)}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Huỷ</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving}>
          {saving ? <Spinner size="sm" className="me-1" /> : null}
          {isEdit ? 'Cập nhật' : 'Tạo trang'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

// ─── Page Detail (Section Builder) ───────────────────────────────────────────
function PageDetail({ page, onBack, onRefresh }) {
  const [sections, setSections] = useState([]);
  const [loadingSections, setLoadingSections] = useState(true);
  const [showSectionModal, setShowSectionModal] = useState(false);
  const [editingSection, setEditingSection] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [reordering, setReordering] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [broadcastLoading, setBroadcastLoading] = useState(false);
  const [readStats, setReadStats] = useState(null);
  const [currentPage, setCurrentPage] = useState(page);

  useEffect(() => {
    refreshSections();
    if (page.requires_acknowledgement) loadReadStats();
  }, []);

  const refreshSections = async () => {
    try {
      setLoadingSections(true);
      const updated = await pagesAPI.getPageById(currentPage.id);
      setSections(updated.sections || []);
      setCurrentPage(updated);
    } catch (err) {
      toast.error('Không thể tải sections');
    } finally {
      setLoadingSections(false);
    }
  };

  const loadReadStats = async () => {
    try {
      const stats = await pagesAPI.getReadStats(currentPage.id);
      setReadStats(stats);
    } catch {
      // non-critical
    }
  };

  const handleBroadcast = async () => {
    if (!window.confirm('Tất cả nhân viên sẽ thấy thông báo đọc lại trang này. Tiếp tục?')) return;
    try {
      setBroadcastLoading(true);
      await pagesAPI.broadcastPage(currentPage.id);
      toast.success('Đã broadcast! Nhân viên sẽ thấy nhắc nhở đọc lại.');
      setReadStats(null);
      await loadReadStats();
    } catch (err) {
      toast.error(err?.message || 'Không thể broadcast');
    } finally {
      setBroadcastLoading(false);
    }
  };

  const handleDeleteSection = async (sectionId) => {
    if (!window.confirm('Xoá section này?')) return;
    try {
      setDeleting(sectionId);
      await pagesAPI.deleteSection(currentPage.id, sectionId);
      toast.success('Đã xoá section');
      refreshSections();
    } catch (err) {
      toast.error(err?.message || 'Không thể xoá');
    } finally {
      setDeleting(null);
    }
  };

  const handleMove = async (index, direction) => {
    const newSections = [...sections];
    const target = index + direction;
    if (target < 0 || target >= newSections.length) return;
    [newSections[index], newSections[target]] = [newSections[target], newSections[index]];
    setSections(newSections);
    try {
      setReordering(true);
      await pagesAPI.reorderSections(
        currentPage.id,
        newSections.map((s, i) => ({ id: s.id, order: i })),
      );
    } catch (err) {
      toast.error('Không thể lưu thứ tự');
      refreshSections();
    } finally {
      setReordering(false);
    }
  };

  const handleTogglePublish = async () => {
    try {
      setPublishLoading(true);
      const updated = await pagesAPI.togglePublish(currentPage.id);
      setCurrentPage((p) => ({ ...p, is_published: updated.is_published }));
      toast.success(updated.is_published ? 'Đã xuất bản' : 'Đã ẩn trang');
      onRefresh();
    } catch (err) {
      toast.error(err?.message || 'Không thể thay đổi trạng thái');
    } finally {
      setPublishLoading(false);
    }
  };

  const typeInfo = (type) => SECTION_TYPES.find((t) => t.value === type);

  return (
    <>
      <div className="d-flex align-items-center flex-wrap gap-2 mb-3">
        <Button variant="outline-secondary" size="sm" onClick={onBack}>← Quay lại</Button>
        <h5 className="mb-0 flex-grow-1">{currentPage.title}</h5>
        <Badge bg="secondary" className="me-1">/info/{currentPage.slug}</Badge>
        {currentPage.requires_acknowledgement && (
          <Badge bg="warning" text="dark" className="me-1">
            <ExclamationDiamond className="me-1" />Bắt buộc đọc
          </Badge>
        )}
        {readStats && (
          <OverlayTrigger overlay={<Tooltip>Số nhân viên đủ điều kiện (active tại thời điểm broadcast) đã xác nhận đọc — version {readStats.broadcast_version}</Tooltip>}>
            <Badge bg="info" className="me-1" style={{ cursor: 'default' }}>
              <PeopleFill className="me-1" />
              {readStats.acked_count}/{readStats.eligible_count ?? readStats.total_active_employees} đã đọc
            </Badge>
          </OverlayTrigger>
        )}
        {currentPage.requires_acknowledgement && (
          <Button
            variant="outline-warning"
            size="sm"
            onClick={handleBroadcast}
            disabled={broadcastLoading}
            title="Yêu cầu tất cả nhân viên đọc lại trang này"
          >
            {broadcastLoading ? <Spinner size="sm" className="me-1" /> : <Megaphone className="me-1" />}
            Broadcast lại
          </Button>
        )}
        <Button
          variant={currentPage.is_published ? 'warning' : 'success'}
          size="sm"
          onClick={handleTogglePublish}
          disabled={publishLoading}
        >
          {publishLoading ? <Spinner size="sm" /> : currentPage.is_published ? <><EyeSlash className="me-1" />Ẩn trang</> : <><Eye className="me-1" />Xuất bản</>}
        </Button>
      </div>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <span className="fw-semibold">Nội dung trang ({sections.length} sections)</span>
          <Button
            variant="primary"
            size="sm"
            onClick={() => { setEditingSection(null); setShowSectionModal(true); }}
          >
            <Plus className="me-1" />Thêm section
          </Button>
        </Card.Header>
        <Card.Body className="p-0">
          {loadingSections ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : sections.length === 0 ? (
            <div className="text-center text-muted py-5">
              <AlignLeft size={32} className="mb-2" />
              <p>Chưa có section nào. Nhấn "Thêm section" để bắt đầu.</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <tbody>
                {sections.map((sec, idx) => {
                  const t = typeInfo(sec.type);
                  return (
                    <tr key={sec.id}>
                      <td style={{ width: 30 }} className="align-middle text-muted">
                        <GripVertical />
                      </td>
                      <td style={{ width: 120 }} className="align-middle">
                        <Badge bg={t?.color || 'secondary'}>{t?.label || sec.type}</Badge>
                      </td>
                      <td className="align-middle">
                        <SectionPreview section={sec} />
                      </td>
                      <td style={{ width: 160 }} className="align-middle">
                        <ButtonGroup size="sm">
                          <Button variant="outline-secondary" onClick={() => handleMove(idx, -1)} disabled={idx === 0 || reordering} title="Lên">
                            <ArrowUp />
                          </Button>
                          <Button variant="outline-secondary" onClick={() => handleMove(idx, 1)} disabled={idx === sections.length - 1 || reordering} title="Xuống">
                            <ArrowDown />
                          </Button>
                          <Button variant="outline-primary" onClick={() => { setEditingSection(sec); setShowSectionModal(true); }} title="Sửa">
                            <PencilSquare />
                          </Button>
                          <Button
                            variant="outline-danger"
                            onClick={() => handleDeleteSection(sec.id)}
                            disabled={deleting === sec.id}
                            title="Xoá"
                          >
                            {deleting === sec.id ? <Spinner size="sm" /> : <Trash />}
                          </Button>
                        </ButtonGroup>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <SectionEditorModal
        show={showSectionModal}
        onHide={() => setShowSectionModal(false)}
        section={editingSection}
        pageId={currentPage.id}
        onSaved={refreshSections}
      />
    </>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function PagesManagementPage() {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('');
  const [showPageModal, setShowPageModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [selectedPage, setSelectedPage] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    loadPages();
  }, [categoryFilter]);

  const loadPages = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await pagesAPI.getAllPages(categoryFilter);
      setPages(res || []);
    } catch (err) {
      setError('Không thể tải danh sách trang');
      toast.error(err?.message || 'Lỗi tải dữ liệu');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Xoá trang này? Tất cả nội dung sẽ bị xoá vĩnh viễn.')) return;
    try {
      setDeletingId(id);
      await pagesAPI.deletePage(id);
      toast.success('Đã xoá trang');
      setPages((prev) => prev.filter((p) => p.id !== id));
      if (selectedPage?.id === id) setSelectedPage(null);
    } catch (err) {
      toast.error(err?.message || 'Không thể xoá trang');
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePublish = async (page) => {
    try {
      const updated = await pagesAPI.togglePublish(page.id);
      setPages((prev) => prev.map((p) => p.id === page.id ? { ...p, is_published: updated.is_published } : p));
      toast.success(updated.is_published ? 'Đã xuất bản' : 'Đã ẩn trang');
    } catch (err) {
      toast.error(err?.message || 'Không thể thay đổi trạng thái');
    }
  };

  if (selectedPage) {
    return (
      <PageLayout>
        <Row>
          <Col>
            <PageDetail
              page={selectedPage}
              onBack={() => { setSelectedPage(null); loadPages(); }}
              onRefresh={loadPages}
            />
          </Col>
        </Row>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="mb-0">Quản lý trang nội dung</h4>
          <small className="text-muted">Tạo và chỉnh sửa các trang cho staff app (nội quy, điều khoản, thông báo...)</small>
        </Col>
        <Col xs="auto">
          <Button variant="primary" onClick={() => { setEditingPage(null); setShowPageModal(true); }}>
            <Plus className="me-1" />Tạo trang mới
          </Button>
        </Col>
      </Row>

      <Row className="mb-3">
        <Col md={4}>
          <Form.Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
            <option value="">Tất cả danh mục</option>
            {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
          </Form.Select>
        </Col>
      </Row>

      {error && <Alert variant="danger">{error}</Alert>}

      <Card>
        <Card.Body className="p-0">
          {loading ? (
            <div className="text-center py-5"><Spinner /></div>
          ) : pages.length === 0 ? (
            <div className="text-center text-muted py-5">
              <p>Chưa có trang nào. Nhấn "Tạo trang mới" để bắt đầu.</p>
            </div>
          ) : (
            <Table responsive hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th>Tiêu đề</th>
                  <th>Slug</th>
                  <th>Danh mục</th>
                  <th>Sections</th>
                  <th>Cài đặt</th>
                  <th>Trạng thái</th>
                  <th style={{ width: 180 }}>Thao tác</th>
                </tr>
              </thead>
              <tbody>
                {pages.map((page) => (
                  <tr key={page.id}>
                    <td className="align-middle fw-semibold">{page.title}</td>
                    <td className="align-middle">
                      <code className="small">/info/{page.slug}</code>
                    </td>
                    <td className="align-middle">
                      <Badge bg="secondary">{page.category}</Badge>
                    </td>
                    <td className="align-middle text-center">
                      {page._count?.sections ?? 0}
                    </td>
                    <td className="align-middle">
                      {page.requires_acknowledgement ? (
                        <Badge bg="warning" text="dark" className="small">
                          <ExclamationDiamond className="me-1" />Bắt buộc đọc
                        </Badge>
                      ) : (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                    <td className="align-middle">
                      <Badge bg={page.is_published ? 'success' : 'secondary'}>
                        {page.is_published ? 'Đã xuất bản' : 'Nháp'}
                      </Badge>
                    </td>
                    <td className="align-middle">
                      <ButtonGroup size="sm">
                        <Button
                          variant="outline-info"
                          onClick={() => setSelectedPage(page)}
                          title="Chỉnh sửa nội dung"
                        >
                          <PencilSquare />
                        </Button>
                        <Button
                          variant={page.is_published ? 'outline-warning' : 'outline-success'}
                          onClick={() => handleTogglePublish(page)}
                          title={page.is_published ? 'Ẩn trang' : 'Xuất bản'}
                        >
                          {page.is_published ? <EyeSlash /> : <Eye />}
                        </Button>
                        <Button
                          variant="outline-secondary"
                          onClick={() => { setEditingPage(page); setShowPageModal(true); }}
                          title="Sửa thông tin"
                        >
                          <GripVertical />
                        </Button>
                        <Button
                          variant="outline-danger"
                          onClick={() => handleDelete(page.id)}
                          disabled={deletingId === page.id}
                          title="Xoá"
                        >
                          {deletingId === page.id ? <Spinner size="sm" /> : <Trash />}
                        </Button>
                      </ButtonGroup>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <PageEditorModal
        show={showPageModal}
        onHide={() => setShowPageModal(false)}
        page={editingPage}
        onSaved={loadPages}
      />
    </PageLayout>
  );
}
